import { useContext, useEffect, useMemo } from 'react';
import { MobXProviderContext } from 'mobx-react';
import type {
  BasketItem,
  InvoicePreview,
  InvoicePreviewError,
} from '@portal/types/nexudus';

/**
 * Hook to access checkout store basket data and actions.
 *
 * IMPORTANT: Components using this hook must be wrapped with MobX `observer()`
 * to react to observable changes. The `data` object provides direct access to
 * store observables - these are accessed during render and tracked by MobX.
 *
 * The `actions` are memoized since they're stable function references.
 */
export function useBasket() {
  const stores = useContext(MobXProviderContext) as Record<string, any>;

  if (!stores?.checkoutStore) {
    throw new Error('useBasket must be used within a MobX Provider');
  }

  const store = stores.checkoutStore;

  // Hydrate basket from localStorage after mount
  useEffect(() => {
    if (!store.hasLoadedFromLocalStorage) {
      store.loadFromLocalStorage();
    }
  }, [store]);

  // Memoize only the actions since they're stable references
  // Data is accessed directly from store so MobX tracking works
  const actions = useMemo(
    () => ({
      addToBasket: store.addToBasket as (params: {
        type: string;
        data: Record<string, any>;
        bookingPrice?: any;
        resourceProducts?: any[];
        previewInvoice?: boolean;
      }) => void,
      removeFromBasket: store.removeFromBasket as (params: {
        item: BasketItem;
        previewInvoice?: boolean;
      }) => void,
      clearInvoicePreview: store.clearInvoicePreview as () => void,
      clearBasket: store.clearBasket as () => void,
      applyDiscount: store.applyDiscount as (code: string | null) => void,
      loadInvoicePreview: store.loadInvoicePreview as (
        createZeroValueInvoice?: boolean,
        options?: { addNewContracts?: boolean; proposalId?: string | null },
      ) => Promise<any>,
      loadBasketInvoice: store.loadBasketInvoice as (params?: {
        proposalId?: string;
      }) => Promise<any>,
      loadFromLocalStorage: store.loadFromLocalStorage as () => void,
    }),
    [store],
  );

  // Return data directly from store (not memoized) so MobX can track access
  // Components must be wrapped with observer() to react to changes
  return {
    data: {
      items: store.basket as BasketItem[],
      invoicePreview: store.invoicePreview as
        | (InvoicePreview & Partial<InvoicePreviewError>)
        | null,
      discountCode: store.discountCode as string | null,
      calculatedBasketPrice: store.calculatedBasketPrice as number,
      hasPlan: store.hasPlan as boolean,
      selectedPlans: store.selectedPlans as string[],
    },
    loadingStates: {
      isLoadingInvoicePreview: store.isLoadingInvoicePreview as boolean,
      isLoadingBasketInvoice: store.isLoadingBasketInvoice as boolean,
    },
    actions,
  };
}
